import { useTranslation } from "react-i18next";
import { userAPI } from "../../UserAPI";
import { useEffect, useId, useState } from "react";
import { TreeState, TreeTable } from "cp-react-tree-table";
import { Folder, FolderOpen } from "lucide-react";

import ReactApexChart from "react-apexcharts";
import { useCustomerTheme } from "../../Theme";
import Metric from "../../components/Metric";
import EnergyDemandEstimate from "./EnergyDemandEstimate";
import { fetchTgaConsumption, TgaConsumption } from "../../PVPotentialAPI";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

class Properties {
  building: any;
}

const _numberFormat = new Intl.NumberFormat('de');
const _numberFormat2 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numberFormat0 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function EnergyAndCO2Properties(properties: Properties) {
  const { t } = useTranslation();
  const [_din276Metadata, setDIN276Metadata] = useState([]) as any;
  const { theme } = useCustomerTheme();
  const [totalArea, _setTotalArea] = useState<number | null>(null);
  const [tga, setTga] = useState<TgaConsumption | null>(null);
  const [tgaError, setTgaError] = useState<string | null>(null);
  const [totalEnergyConsumption, setEnergyConsumption] = useState(0) as any;
  const [totalEnergyIntensity, setEnergyIntensity] = useState(0) as any;
  const [totalCO2Consumption, setCO2Consumption] = useState(0) as any;
  const [totalCO2Intensity, setCO2Intensity] = useState(0) as any;
  const [_assets, setAssets] = useState([]) as any;
  const [tree, setTree] = useState([]/*MOCK_DATA*/) as any;
  const [treeData, setTreeData] = useState(TreeState.create([]/*MOCK_DATA*/)) as any;
  const [expanded, setExpanded] = useState(false);
  const [co2Equivalent, setCO2Equivalent] = useState(0.38);
  const [colors] = useState([theme.secondary, theme.tertiary, theme.primary, theme.secondaryLight, theme.tertiaryLight, theme.primaryLight]);
  const [seriesLabels, setSeriesLabels] = useState([]) as any;
  const [totalSeries, setTotalSeries] = useState([]) as any;
  const [perCategorySeries, setPerCategorySeries] = useState([]) as any;
  const [usageProfile, setUsageProfile] = useState<string>('office');
  const filterId = useId();
  const usageProfileId = useId();
  const co2EquivalentId = useId();

  const usageProfileOptions = [
    { key: 'office', text: t('buildingProperties.energyManagementProperties.usageProfile.office') },
    { key: 'logisticsAndStorage', text: t('buildingProperties.energyManagementProperties.usageProfile.logisticsAndStorage') },
    { key: 'production', text: t('buildingProperties.energyManagementProperties.usageProfile.production') },
    { key: 'publicUse', text: t('buildingProperties.energyManagementProperties.usageProfile.publicUse') }
  ];

  const handleUsageProfileChange = (value: string) => {
    if (value) {
      setUsageProfile(value);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const call = async () => {
      const din276Metadata = await userAPI.getDIN276Metadata();
      setDIN276Metadata(din276Metadata);

      const buildingId = properties.building?._id ? String(properties.building._id) : properties.building?.id;
      if (!buildingId) {
        return;
      }
      // Clear stale data from previous building
      setTga(null);
      setTgaError(null);
      _setTotalArea(null);
      setAssets([]);
      
      // Hochrechnung auf dem Server: die Anlagen dieses Gebäudes (nicht der Postleitzahl), Leistung × Volllaststunden,
      // Fläche aus dem 3D-Modell statt einer festen Zahl.
      let tga: TgaConsumption;
      try {
        tga = await fetchTgaConsumption(buildingId);
      } catch (e: any) {
        // Nicht verschlucken: ohne Antwort bliebe die Tabelle leer, ohne dass jemand den Grund sieht
        console.warn('[energie] TGA-Hochrechnung fehlgeschlagen', buildingId, e);
        if (!cancelled) setTgaError(e?.message ?? String(e));
        return;
      }
      if (cancelled) {
        return;
      }
      setTgaError(null);
      setTga(tga);
      if (tga.areaM2) _setTotalArea(tga.areaM2);
      const newCO2Equivalent = tga.totals.kWh > 0 ? Math.round((tga.totals.co2Kg / tga.totals.kWh) * 1000) / 1000 : co2Equivalent;
      if (tga.totals.kWh > 0) setCO2Equivalent(newCO2Equivalent);
      // Nur technische Anlagen (KG 400): Türen, Wände und Feuerlöscher sind keine Verbraucher und füllten die Tabelle mit Nullen
      const assets = tga.assets
        .filter(asset => asset.code && asset.technical)
        .map(asset => ({ _id: asset.assetId, name: asset.name, schemaCategory: asset.code as string, energyConsumption: asset.kWh, accuracy: asset.accuracy }));

      setAssets(assets);

      const tree: any = buildTree(assets, din276Metadata, tga.areaM2 ?? 0, newCO2Equivalent);

      setTree(tree);
      setTreeData(TreeState.create(tree));
    };

    call();

    return () => {
      cancelled = true;
    };
    // Primitive deps: `properties` is a new object on every parent render (tri-server#1550).
  }, [properties.building?._id, properties.building?.id]);

  const buildTree = (assets: any, din276Metadata: any, area: number, co2Factor: number = co2Equivalent) => {
    let newConsumptionPerCategory: any = {};
    let totalConsumption = 0;
    let totalCO2Equivalent = 0;

    const tree = assets.reduce((tree: any, asset: any) => {
      const category = asset.schemaCategory.split('.')[0];
      const categoryMetadata = din276Metadata.find((metadata: any) => metadata.code === category);

      // TODO Could also truncate
      const categoryName = categoryMetadata && categoryMetadata.name ? categoryMetadata.name.split(' ')[0] : category;

      tree[category] = tree[category] || { data: { name: `${category} ${categoryName}`, consumption: 0, co2: co2Factor * 4711 }, children: [] };

      let slot = tree[category].children.find((slot: any) => slot.data.category === asset.schemaCategory);

      if (!slot) {
        const subCategoryMetadata = din276Metadata.find((metadata: any) => metadata.code === asset.schemaCategory);

        if (!subCategoryMetadata) {
          console.log('Cannot find category >>>', asset.schemaCategory);

          return tree;
        }

        // TODO Could also truncate
        const subCategoryName = subCategoryMetadata.name.split(' ')[0];

        slot = { data: { category: asset.schemaCategory, name: `${asset.schemaCategory} ${subCategoryName}`, consumption: 0, accuracy: asset.accuracy ?? 1, co2: 0, count: 1 } };

        tree[category].children.push(slot);
      } else {
        ++slot.data.count;
        // die Zeile ist so genau wie ihre ungenaueste Anlage
        slot.data.accuracy = Math.min(slot.data.accuracy, asset.accuracy ?? 1);
      }

      slot.data.consumption += asset.energyConsumption;
      slot.data.co2 = slot.data.consumption * co2Factor;
      tree[category].data.consumption += asset.energyConsumption;
      tree[category].data.co2 = tree[category].data.consumption * co2Factor;
      totalConsumption += asset.energyConsumption;
      totalCO2Equivalent = totalConsumption * co2Factor;

      setEnergyConsumption(totalConsumption / 1000);
      setEnergyIntensity(area ? totalConsumption / area : 0);
      setCO2Consumption(totalConsumption * co2Factor / 1000);
      setCO2Intensity(area ? totalConsumption * co2Factor / area : 0);

      if (asset.energyConsumption > 0) {
        newConsumptionPerCategory[category] = newConsumptionPerCategory[category] || {
          label: categoryMetadata.name,
          consumption: 0,
          count: 0
        };

        newConsumptionPerCategory[category].count += 1;
        newConsumptionPerCategory[category].consumption += asset.energyConsumption;
      }

      return tree;
    }, {});

    setSeriesLabels(Object.keys(newConsumptionPerCategory).map((key: any) => newConsumptionPerCategory[key].label));
    setTotalSeries(Object.keys(newConsumptionPerCategory).map((key: string) => newConsumptionPerCategory[key].consumption));
    setPerCategorySeries(Object.keys(newConsumptionPerCategory).map((key: string) => newConsumptionPerCategory[key].consumption / newConsumptionPerCategory[key].count));

    return [{ data: { total: true, consumption: totalConsumption, co2: totalCO2Equivalent } }, ...Object.values(tree)];
  }

  const applyCO2Equivalent = (equivalent: number) => {
    for (const category of tree) {
      category.data.co2 = equivalent * category.data.consumption;

      if (category.children) {
        for (const slot of category.children) {
          slot.co2 = equivalent * slot.consumption;
        }
      }
    }

    setTree(tree);
    setTreeData(TreeState.create(tree));
  };

  const handleOnExpandAll = () => {
    setTreeData(TreeState.expandAll(treeData));
    setExpanded(true);
  };

  const handleOnCollapseAll = () => {
    setTreeData(TreeState.collapseAll(treeData));
    setExpanded(false);
  };

  const handleOnChange = (newValue: any) => {
    setTreeData(newValue);
  }

  const renderIndexCell = (row: any) => {
    if (row.data.total) {
      return <div className="my-1 font-bold">{t('ui.total')}</div>
    } else {
      /* `with-children` / `without-children` / `toggle-button` bleiben: die
         Baumtabelle (cp-react-tree-table) wird bewusst nicht ersetzt und ihre
         Auf-/Zuklapp-Optik hängt an diesen Klassen. */
      return <div style={{ paddingLeft: (row.metadata.depth * 15) + 'px' }}
        className={row.metadata.hasChildren ? 'w-[300px] max-w-full with-children' : 'w-[400px] max-w-full without-children'}>

        {(row.metadata.hasChildren)
          ? (
            <button type="button" className="toggle-button" onClick={row.toggleChildren}></button>
          )
          : ''
        }

        <span>{row.data.name}</span>
      </div>
    }
  };

  const renderProductCell = (row: any) => {
    if (row.children) {
      return <div></div>;
    } else {
      return <span>{row.data.product || '-'}</span>;
    }
  }

  const renderCountCell = (row: any) => {
    return <div className={`flex-1 text-right ${row.data.total ? 'my-1 font-bold' : ''}`}>{row.data.count}</div>
  }

  const renderConsumptionCell = (row: any) => {
    const showAccuracy = !row.children && row.data.accuracy > 0;

    return <div className={`flex-1 text-right ${row.data.total ? 'my-1 font-bold' : ''}`}>{numberFormat0.format(row.data.consumption)} {showAccuracy ? <sup>{row.data.accuracy}</sup> : <sup>&nbsp;</sup>}</div>;
  }

  const renderCO2Cell = (row: any) => {
    return <div className={`flex-1 text-right ${row.data.total ? 'my-1 font-bold' : ''}`}>{numberFormat0.format(row.data.co2)}</div>;
  }

  console.log('Labels >>>', seriesLabels);
  console.log('Total Series >>>', totalSeries);
  console.log('Per Category Series >>>', perCategorySeries);

  return <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
    <EnergyDemandEstimate building={properties.building} />
    <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">{t('ui.energyAndCo2Balance')}</h3>
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex min-w-0 flex-col">
        <div className="text-lg font-bold">{t('ui.total')}</div>
        <div className="overflow-x-auto">
          <ReactApexChart options={{ colors, chart: {}, legend: { offsetX: -10 }, labels: seriesLabels }} series={totalSeries} type="pie" height={250} width={500} />
        </div>
      </div>
      <div className="flex min-w-0 flex-col">
        <div className="text-lg font-bold">{t('ui.perUnitInDeviceClass')}</div>
        <div className="overflow-x-auto">
          <ReactApexChart options={{ colors, chart: {}, legend: { offsetX: -10 }, labels: seriesLabels }} series={perCategorySeries} type="pie" height={250} width={500} />
        </div>
      </div>
    </div>
    <div className='flex flex-wrap gap-4'>
      <Metric title={t('ui.buildingArea')} value={totalArea ? numberFormat0.format(totalArea) : '—'} unit={'m²'} color={theme.tertiaryDarker}></Metric>
      <Metric title={t('ui.totalEnergyConsumption')} value={numberFormat0.format(totalEnergyConsumption)} unit={'MWh/a'} color={theme.secondaryLight}></Metric>
      <Metric title={t('ui.energyIntensity')} value={numberFormat0.format(totalEnergyIntensity)} unit={'kWh/m²/a'} color={theme.tertiary}></Metric>
      <Metric title={t('ui.co2Emissions')} value={numberFormat0.format(totalCO2Consumption)} color={theme.primary} unit={'t/a'}></Metric>
      <Metric title={t('ui.co2Intensity')} value={numberFormat0.format(totalCO2Intensity)} unit={'kg/m²/a'} color={theme.secondary}></Metric>
    </div>
    {tgaError && <p className="text-sm text-destructive">Die Hochrechnung aus der Technik konnte nicht geladen werden: {tgaError}</p>}
    {tga && !tga.technical && !tga.heatFromModel && <p className="text-sm text-muted-foreground">An diesem Gebäude sind für deinen Mandanten keine technischen Anlagen erfasst.</p>}
    {tga && <p className="text-xs text-muted-foreground">
      Hochgerechnet aus {tga.counted} von {tga.technical} technischen Anlagen ({tga.assets.length} erfasst){tga.typical ? `, davon ${tga.typical} mit Typwert` : ''}{tga.withoutPower ? `, ${tga.withoutPower} ohne Leistungsangabe` : ''}{tga.heatFromModel ? '; Wärme aus dem Energiemodell, weil kein Wärmeerzeuger erfasst ist' : ''}
      {tga.areaSource ? `; Fläche: ${tga.areaSource}` : '; ohne Fläche keine Kennwerte je m²'}. {tga.caveat}
    </p>}
    <div className="flex flex-wrap items-end gap-4">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={expanded ? t('ui.collapse') : t('ui.expand')}
        onClick={expanded ? handleOnCollapseAll : handleOnExpandAll}
      >
        {expanded
          ? <Folder aria-hidden="true" className="h-4 w-4" />
          : <FolderOpen aria-hidden="true" className="h-4 w-4" />}
      </Button>
      {/* Das Filterfeld war schon im Bestand ohne Wirkung (kein onChange). */}
      <div className="flex min-w-0 flex-col gap-1.5">
        <Label htmlFor={filterId}>Filter</Label>
        <Input id={filterId} className="w-[250px] max-w-full" placeholder="Filter eingeben" />
      </div>
      <div className="flex min-w-0 flex-col gap-1.5">
        <Label htmlFor={usageProfileId}>{t('buildingProperties.energyManagementProperties.usageProfile.label')}</Label>
        <Select value={usageProfile} onValueChange={handleUsageProfileChange}>
          <SelectTrigger id={usageProfileId} className="w-[300px] max-w-full">
            <SelectValue placeholder={t('global.pleaseSelect')} />
          </SelectTrigger>
          <SelectContent className="max-w-[calc(100vw-2rem)]">
            {usageProfileOptions.map((option) => (
              <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                {option.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex min-w-0 flex-col gap-1.5 sm:ml-auto">
        <Label htmlFor={co2EquivalentId}>{t('ui.co2Equivalent')}</Label>
        <div className="flex items-center gap-2">
          <Input
            id={co2EquivalentId}
            type="number"
            step={0.1}
            className="w-[100px]"
            value={`${co2Equivalent}`}
            onChange={(event) => {
              const value = event.target.value;
              console.log('Value', value);
              setCO2Equivalent(Number(value));
              applyCO2Equivalent(Number(value));
            }}
          />
          <div className="text-sm">kg/kWh</div>
        </div>
      </div>
    </div>
    {/* Die Baumtabelle ist breiter als schmale Viewports; sie scrollt innerhalb
        ihres Rahmens statt die Seite aufzudrücken. */}
    <div className="overflow-x-auto">
      <TreeTable
        value={treeData}
        onChange={handleOnChange}>
        <TreeTable.Column
          renderCell={renderIndexCell}
          renderHeaderCell={() => <span>Verbrauchertyp</span>}
          grow={1}
          basis={'500px'} />
        <TreeTable.Column
          renderCell={renderProductCell}
          renderHeaderCell={() => <span>Produkt</span>}
          grow={1}
          basis={'230px'} />
        <TreeTable.Column
          renderCell={renderCountCell}
          renderHeaderCell={() => <span>Anzahl</span>}
          grow={0}
          basis={'100px'} />
        <TreeTable.Column
          renderCell={renderConsumptionCell}
          renderHeaderCell={() => <span>Verbrauch (kWh/a)</span>}
          grow={0}
          basis={'180px'} />
        <TreeTable.Column
          renderCell={renderCO2Cell}
          renderHeaderCell={() => <span>CO₂ (kg/a)</span>}
          grow={0}
          basis={'180px'} />
      </TreeTable>
    </div>
    <div className="text-xs"><sup>1</sup> {t('ui.estimatedFromDeviceType')}&nbsp;&nbsp;<sup>2</sup> {t('ui.estimatedFromTypeProductInfo')}&nbsp;&nbsp;<sup>3</sup> {t('ui.measured')}</div>
  </div >;
}
